// 倉庫番ゲームの Ray cast 集約モジュール
//
// 設計方針（common/model/animations/character/move.ts, jump.ts と同じ）：
// - 下向き ray を主に使う。前マスの XZ から下向き、origin Y = char.y + 2、
//   distance で物理判定（前進: 2.5 / ジャンプ: 3.5）
// - distance 2.5 で hit しなければ「越えられない壁 or 落差大 or 何もない」
//   → 別途 wide ray (distance 15) で void / wall / drop / river を区別
// - mesh.name による predicate（`{type}_asset` 規則）で拾う mesh をフィルタ

import { Scene } from "@babylonjs/core/scene";
import { Ray } from "@babylonjs/core/Culling/ray";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MAP_MESH_SIZE, BUTTON_ASSET_SIZE } from "common/model";

export const HEIGHT_EPSILON = 0.05;
export const STEP_MAX = 1.0;
export const CEILING_CLEARANCE = 1.5;

// common と同じ ray 距離（物理判定に意味を持たせる）
export const RAY_DISTANCE_FORWARD = 2.5;
export const RAY_DISTANCE_JUMP = 3.5;

export type TileKind = "void" | "river" | "wall" | "step" | "drop" | "floor" | "box";

export type FrontProbe = {
  kind: TileKind;
  topY: number;
  hitMesh: AbstractMesh | null;
};

// === predicate（mesh.name のみで判定） ===
export const isGround = (m: AbstractMesh) =>
  m.name.includes("asset") && !m.name.includes("condition");
export const isButton = (m: AbstractMesh) => m.name.includes("condition");

// === 前マスの種別判定（common 準拠の下向き ray） ===
// effectiveCharY: ボタン補正後のキャラ実効床面 Y
// rayDistance: 前進=2.5、ジャンプ=3.5（drop 検出のため）
export const probeFront = (
  scene: Scene,
  charPos: Vector3,
  fwd: { dx: number; dz: number },
  effectiveCharY: number,
  rayDistance: number,
): FrontProbe => {
  const targetX = charPos.x + fwd.dx * MAP_MESH_SIZE;
  const targetZ = charPos.z + fwd.dz * MAP_MESH_SIZE;

  // common 同様: origin = (前マス XZ, char.y + 2), 下向き ray
  const origin = new Vector3(targetX, effectiveCharY + 2, targetZ);
  const ray = new Ray(origin, new Vector3(0, -1, 0), rayDistance);
  const pick = scene.pickWithRay(ray, isGround);

  if (pick?.hit && pick.pickedMesh) {
    // hit = 移動可能範囲内のタイル（floor / step / box / river）
    const outer = (pick.pickedMesh.parent as AbstractMesh) ?? (pick.pickedMesh as AbstractMesh);
    const topY = outer.getBoundingInfo().boundingBox.maximumWorld.y;

    // 種別判定: mesh.name 優先、その後 diff で classify
    if (outer.name === "river_asset") return { kind: "river", topY, hitMesh: outer };
    if (outer.name === "box_asset") return { kind: "box", topY, hitMesh: outer };

    const diff = Math.round((topY - effectiveCharY) * 10) / 10;
    if (Math.abs(diff) < HEIGHT_EPSILON) return { kind: "floor", topY, hitMesh: outer };
    if (diff > 0 && diff <= STEP_MAX) return { kind: "step", topY, hitMesh: outer };
    if (diff < 0 && Math.abs(diff) <= STEP_MAX) return { kind: "drop", topY, hitMesh: outer };
    // 範囲外（hit したが diff 規則外）
    return { kind: "wall", topY, hitMesh: outer };
  }

  // ray が届かない = 越えられない壁 / 大落差 / void のいずれか
  // wide ray (distance 15) で何があるか確認
  const wideOrigin = new Vector3(targetX, effectiveCharY + 5, targetZ);
  const wideRay = new Ray(wideOrigin, new Vector3(0, -1, 0), 15);
  const widePick = scene.pickWithRay(wideRay, isGround);

  if (!widePick?.hit || !widePick.pickedMesh) {
    return { kind: "void", topY: 0, hitMesh: null };
  }

  const outer =
    (widePick.pickedMesh.parent as AbstractMesh) ?? (widePick.pickedMesh as AbstractMesh);
  const topY = outer.getBoundingInfo().boundingBox.maximumWorld.y;

  // 大落差で hit した先が river なら river 扱い、そうでなければ wall (高すぎ) or drop (低すぎ)
  if (outer.name === "river_asset") return { kind: "river", topY, hitMesh: outer };

  const diff = Math.round((topY - effectiveCharY) * 10) / 10;
  if (diff > STEP_MAX) return { kind: "wall", topY, hitMesh: outer };
  return { kind: "drop", topY, hitMesh: outer };
};

// === ボタン (CONDITION) の有無 ===
// distance: current（足元）は 1、next（前マス）は 15
export const probeButtonAt = (
  scene: Scene,
  x: number,
  y: number,
  z: number,
  distance: number,
): boolean => {
  const ray = new Ray(new Vector3(x, y, z), new Vector3(0, -1, 0), distance);
  return !!scene.pickWithRay(ray, isButton)?.hit;
};

// === current/next のボタン有無 → Y 補正 ===
export const calcOffset = (currentBtn: boolean, nextBtn: boolean): number => {
  if (currentBtn && nextBtn) return 0;
  if (currentBtn) return -BUTTON_ASSET_SIZE.y;
  if (nextBtn) return BUTTON_ASSET_SIZE.y;
  return 0;
};

// === 上空塞判定（ジャンプ着地マスの上に障害物） ===
export const isCeilingBlocked = (
  scene: Scene,
  worldX: number,
  worldZ: number,
  fromY: number,
): boolean => {
  const origin = new Vector3(worldX, fromY + CEILING_CLEARANCE, worldZ);
  const ray = new Ray(origin, new Vector3(0, 1, 0), 0.5);
  return !!scene.pickWithRay(ray, isGround)?.hit;
};
